import './styles.scss';
import * as qs from 'querystring';
import { HiChevronLeft } from '@react-icons/all-files/hi/HiChevronLeft';
import { HiChevronRight } from '@react-icons/all-files/hi/HiChevronRight';
import { ScrollingCarousel } from '@trendyol-js/react-carousel';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Col, Row } from 'reactstrap';
import ProductRefinementList, {
  ClearRefinements,
} from '../ProductRefinementList';
import FilterDialog from './FilterDialog';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { updatedFilters } from '~redux/search/product';
import ProductSearchSortDropdown from '~view/widgets/ProductSearchSortDropdown';

export const MinimumOrderAmount = {
  NO_MINIMUM: 'brand_minimum_buy = 0',
  LESS_THAN_10000: 'brand_minimum_buy <= 10000',
  LESS_THAN_30000: 'brand_minimum_buy <= 30000',
  LESS_THAN_50000: 'brand_minimum_buy <= 50000',
  GREATER_THAN_50000: 'brand_minimum_buy >= 50000',
};

export const MinimumShippingDate = {
  ONE_TO_TWO: `product_estimated_ship_date_min: 1 TO 2`,
  THREE_TO_FIVE: `product_estimated_ship_date_min: 3 TO 5`,
  GREATER_THAN_5: `product_estimated_ship_date_min >= 5`,
  PRE_ORDER: 'product_preorder = 1',
};

export const RetailPrice = {
  ZERO_TO_ONE_K: 'product_retail_price: 0 TO 1000',
  ONE_K_TO_THREE_K: 'product_retail_price: 1000 TO 3000',
  THREE_K_TO_FIVE_K: 'product_retail_price: 3000 TO 5000',
  FIVE_K_TO_TEN_K: 'product_retail_price: 5000 TO 10000',
  TEN_K_TO_TWENTY_K: 'product_retail_price: 10000 TO 20000',
  TWENTY_K_TO_THIRTY_K: 'product_retail_price: 20000 TO 30000',
  GREATER_THAN_THIRTY_K: 'product_retail_price >= 30000',
};

export const WholeSaleRate = {
  ZERO_TO_TEN: 'wholesale_rate: 0 TO 10',
  ELEVEN_TO_TWENTY: 'wholesale_rate: 11 TO 20',
  TWENTY_ONE_TO_THIRTY: 'wholesale_rate: 21 TO 30',
  THIRTY_ONE_TO_FORTY: 'wholesale_rate: 31 TO 40',
  FORTY_ONE_TO_FIFTY: 'wholesale_rate: 41 TO 50',
  FIFTY_ONE_TO_SIXTY: 'wholesale_rate: 51 TO 60',
  SIXTY_ONE_TO_SEVENTY: 'wholesale_rate: 61 TO 70',
};

export const ProductSeason = {
  NOTHING: 'product_season: ""',
  SPRING_SUMMER: 'product_season: SS',
  AUTUMN_WINTER: 'product_season: AW',
};

export const Filters: Record<
  string,
  {
    attribute: string;
    header: string;
    items?: Record<string, string>;
    auth?: boolean;
  }
> = {
  product_subcategory: {
    attribute: 'product_subcategory',
    header: 'カテゴリー',
  },
  brand_tags: {
    attribute: 'brand_tags',
    header: 'タグ',
  },
  product_season: {
    attribute: 'product_season',
    header: 'シーズン',
    items: {
      なし: ProductSeason.NOTHING,
      'Spring/Summer': ProductSeason.SPRING_SUMMER,
      'Autumn/Winter': ProductSeason.AUTUMN_WINTER,
    },
  },
  wholesale_rate: {
    attribute: 'wholesale_rate',
    header: '掛け率',
    items: {
      '0〜10%': WholeSaleRate.ZERO_TO_TEN,
      '11〜20%': WholeSaleRate.ELEVEN_TO_TWENTY,
      '21〜30%': WholeSaleRate.TWENTY_ONE_TO_THIRTY,
      '31〜40%': WholeSaleRate.THIRTY_ONE_TO_FORTY,
      '41〜50%': WholeSaleRate.FORTY_ONE_TO_FIFTY,
      '51〜60%': WholeSaleRate.FIFTY_ONE_TO_SIXTY,
      '61〜70%': WholeSaleRate.SIXTY_ONE_TO_SEVENTY,
    },
    auth: true,
  },
  product_retail_price: {
    attribute: 'product_retail_price',
    header: '上代',
    items: {
      '￥0 ~ ￥1000': RetailPrice.ZERO_TO_ONE_K,
      '￥1000 ~ ￥3000': RetailPrice.ONE_K_TO_THREE_K,
      '￥3000 ~ ￥5000': RetailPrice.THREE_K_TO_FIVE_K,
      '￥5000 ~ ￥10000': RetailPrice.FIVE_K_TO_TEN_K,
      '￥10000 ~ ￥20000': RetailPrice.TEN_K_TO_TWENTY_K,
      '￥20000 ~ ￥30000': RetailPrice.TWENTY_K_TO_THIRTY_K,
      '￥30000 ~': RetailPrice.GREATER_THAN_THIRTY_K,
    },
  },
  brand_minimum_buy: {
    attribute: 'brand_minimum_buy',
    header: '最小発注金額',
    items: {
      なし: MinimumOrderAmount.NO_MINIMUM,
      '1万円以下': MinimumOrderAmount.LESS_THAN_10000,
      '3万円以下': MinimumOrderAmount.LESS_THAN_30000,
      '5万円以下': MinimumOrderAmount.LESS_THAN_50000,
      '5万円以上': MinimumOrderAmount.GREATER_THAN_50000,
    },
    auth: true,
  },
  brand_name: {
    attribute: 'brand_name',
    header: 'ブランド',
  },
  product_preorder_shipping_date: {
    attribute: 'product_preorder_shipping_date',
    header: '最短発送日',
    items: {
      '1-2日': MinimumShippingDate.ONE_TO_TWO,
      '3-5日': MinimumShippingDate.THREE_TO_FIVE,
      '5日以上': MinimumShippingDate.GREATER_THAN_5,
      予約注文: MinimumShippingDate.PRE_ORDER,
    },
  },
  product_sizes: {
    attribute: 'product_sizes',
    header: 'サイズ',
  },
  product_colors: {
    attribute: 'product_colors',
    header: 'カラー',
  },
  product_types: {
    attribute: 'product_types',
    header: 'その他種類',
  },
};

const ListFilters: React.FC<{ listId: string }> = ({ listId }) => {
  const auth = useSelector(authUserSelector);

  const dispatch = useDispatch();
  const location = useLocation();
  useEffect(() => {
    const queries = qs.decode(location.search);
    const items = Object.entries(queries)
      .filter(([key, values]) => values && Filters[key])
      .reduce((prev, [key, values]) => {
        prev[key] = Array.isArray(values)
          ? values.reduce((prev, value) => {
              prev[value] = Filters[key]?.items?.[value];
              return prev;
            }, {} as Record<string, string | undefined>)
          : {
              [values!]: Filters[key]?.items?.[values!],
            };
        return prev;
      }, {} as Record<string, Record<string, string | undefined>>);
    dispatch(updatedFilters({ listId, items }));
  }, [listId, location.search]);

  return (
    <Row
      aria-describedby="product-list-filters"
      className="product-list-filters py-2 bg-white"
    >
      <Col className="d-flex justify-content-between flex-nowrap px-2">
        <ScrollingCarousel
          className="list-view-category-slider"
          rightIcon={
            <h5 className="mb-0 right-icon">
              <HiChevronRight size={30} />
            </h5>
          }
          leftIcon={
            <h5 className="mb-0 left-icon">
              <HiChevronLeft size={30} />
            </h5>
          }
        >
          <>
            <div className="refinement-list-select">
              <FilterDialog listId={listId} />
            </div>
            {Object.values(Filters)
              .filter((value) => auth || !value.auth)
              .map((value) => (
                <div className="refinement-list-select">
                  <ProductRefinementList
                    listId={listId}
                    title={value.header}
                    attribute={value.attribute}
                    items={value.items}
                  />
                </div>
              ))}
          </>
          <div className="refinement-list-select">
            <ClearRefinements listId={listId} />
          </div>
        </ScrollingCarousel>
        <div className="sort-select-wrap">
          <ProductSearchSortDropdown listId={listId} />
        </div>
      </Col>
    </Row>
  );
};

export default ListFilters;
